
/**
 * TriageProjectTable Component - Sortable table view for triage projects - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays triage project data in a paginated, sortable table format with modal details.
 * Features intelligent sorting (active projects first), pagination controls, and status indicators.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of triage project data objects
 * @param {number|string} [props.pageSize=10] - Number of items per page or 'All' for no pagination
 * @returns {JSX.Element} The TriageProjectTable component
 * 
 * @example
 * <TriageProjectTable 
 *   data={triageProjects} 
 *   pageSize={25} 
 * />
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import TriageProjectModal from '../../modals/TriageProjectModal';
import { truncateText, removeTime } from '../../utils/textUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSortUp, faSortDown, faSort, faRocket, faMoon } from '@fortawesome/free-solid-svg-icons';
import './TriageProjectPipeline.css';
const TriageProjectTable = ({ data, pageSize: initialPageSize = 10 }) => {
    const [activeModals, setActiveModals] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);

    /**
     * Opens a modal to display project details
     * Handles modal stacking and z-index management for multiple open modals
     * 
     * @param {Object} itemData - Project data object to display in modal
     * @param {string|number} itemData.id - Unique identifier for the project
     */
    const handleShowModal = (itemData) => {
        const existingModalIndex = activeModals.findIndex(modal => modal.id === itemData.id);
            if (existingModalIndex !== -1) {
            const updatedModals = [...activeModals];
            const existingModal = updatedModals[existingModalIndex];

            existingModal.zIndex = activeModals.length + 1;
            existingModal.isOpen = true;

            updatedModals.splice(existingModalIndex, 1);
            updatedModals.push(existingModal);

            setActiveModals(updatedModals);
            } else {
            const newModal = {
                id: itemData.id,
                data: itemData,
                isOpen: true,
                top: `${15 + (activeModals.length * 2)}%`,
                left: `${30 + (activeModals.length * 2)}%`,
                zIndex: activeModals.length + 1,
            };
            setActiveModals(prev => [...prev, newModal]);
        }
    };

    /**
     * Closes a modal by its ID and removes it from active modals
     * 
     * @param {string|number} id - Unique identifier for the modal to close
     */
    const handleCloseModal = (id) => {
        setActiveModals(prev =>
            prev.map(modal =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
        setActiveModals(prev => prev.filter(modal => modal.id !== id));
    };

    /**
     * Generates status display component with appropriate icon and styling
     * 
     * @param {string} status - Project status string
     * @returns {JSX.Element} Formatted status display with icon
     */
    const getStatusDisplay = (status) => {
        if (!status) return 'Unknown Status';
        
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case 'completed':
            case 'complete':
                return (
                    <span className="status-display status-complete">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Complete
                    </span>
                );
            case 'in progress':
            case 'active':
            case 'live':
                return (
                    <span className="status-display status-active">
                        <FontAwesomeIcon icon={faRocket} />
                        {status}
                    </span>
                );
            case 'inactive':
                return (
                    <span className="status-display status-default">
                        <FontAwesomeIcon icon={faMoon} />
                        {status}
                    </span>
                );
            default:
                return (
                    <span className="status-display status-default">
                        <FontAwesomeIcon icon={faRocket} />
                        {status}
                    </span>
                );
        }
    };

    // Pagination calculations
    const paginationInfo = useMemo(() => {
        // Sort data: Live/Active at the top, then most recent dates
        const sortedData = [...data].sort((a, b) => {
            const aStatus = a.status ? a.status.toLowerCase() : '';
            const bStatus = b.status ? b.status.toLowerCase() : '';
            
            if ((aStatus === 'live' || aStatus === 'active') && (bStatus !== 'live' && bStatus !== 'active')) return -1;
            if ((bStatus === 'live' || bStatus === 'active') && (aStatus !== 'live' && aStatus !== 'active')) return 1;
            
            const aDate = a.dateItemAddedtoTriage ? new Date(a.dateItemAddedtoTriage) : new Date(0);
            const bDate = b.dateItemAddedtoTriage ? new Date(b.dateItemAddedtoTriage) : new Date(0);
            return bDate - aDate;
        });

        const itemsPerPage = pageSize === 'All' ? sortedData.length : parseInt(pageSize);
        const totalPages = pageSize === 'All' ? 1 : Math.ceil(sortedData.length / itemsPerPage);
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageData = sortedData.slice(startIndex, endIndex);
        
        return {
            totalItems: sortedData.length,
            totalPages,
            currentPageData: pageData,
            startIndex,
            endIndex
        };
    }, [data, currentPage, pageSize]);

    // Current page data for display
    const visibleData = paginationInfo.currentPageData;
    const totalItems = paginationInfo.totalItems;
    const totalPages = paginationInfo.totalPages;

    // Reset to first page when pageSize changes
    useEffect(() => {
        setCurrentPage(0);
    }, [pageSize, data.length]);

    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;

    /**
     * Navigates to the previous page if available
     */
    const previousPage = () => {
        if (canPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Navigates to the next page if available
     */
    const nextPage = () => {
        if (canNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Updates page size and resets to first page
     * 
     * @param {number|string} newPageSize - New page size value
     */
    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    // React-table columns definition
    const columns = React.useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ value, row }) => (
                    <Tippy content={value || 'N/A'}>
                        <div className="table-cell-content">
                            {truncateText(value || 'N/A', 50)}
                        </div>
                    </Tippy>
                )
            },
            {
                Header: 'Description',
                accessor: 'childName',
                Cell: ({ value, row }) => (
                    <Tippy content={value || 'N/A'}>
                        <div className="table-cell-content">
                            {truncateText(value || 'N/A', 70)}
                        </div>
                    </Tippy>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value, row }) => (
                    <Tippy content={`Status: ${value || 'Unknown'}`}>
                        <div className="table-cell-content">
                            {getStatusDisplay(value)}
                        </div>
                    </Tippy>
                )
            },
            {
                Header: 'Start Date',
                accessor: 'dateItemAddedtoTriage',
                Cell: ({ value }) => (
                    <Tippy content={value || 'No start date'}>
                        <div className="table-cell-content">
                            {value || 'No date'}
                        </div>
                    </Tippy>
                )
            },
            {
                Header: 'End Date',
                accessor: 'closeDate',
                Cell: ({ value }) => (
                    <Tippy content={value ? removeTime(value) : 'No end date'}>
                        <div className="table-cell-content">
                            {value ? removeTime(value) : 'No date'}
                        </div>
                    </Tippy>
                )
            }
        ],
        []
    );

    // React-table hooks
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable(
        {
            columns,
            data: visibleData,
            disableSortRemove: true
        },
        useSortBy
    );

    return (
        <div className="triage-project-table-container">
            {/* Table */}
            <div className="triage-project-table-wrapper">
                {totalItems === 0 ? (
                    <div className="no-results">
                        <div className="no-results-content">
                            <i className="fas fa-search"></i>
                            <h3>No projects found</h3>
                            <p>Try adjusting your search criteria or filters to see more results.</p>
                        </div>
                    </div>
                ) : (
                    <div className="triage-project-table-responsive">
                        <table {...getTableProps()} className="triage-project-table">
                            <thead>
                                {headerGroups.map(headerGroup => {
                                    const { key: headerKey, ...headerProps } = headerGroup.getHeaderGroupProps();
                                    return (
                                        <tr {...headerProps}>
                                            {headerGroup.headers.map(column => {
                                                const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                                                return (
                                                    <th 
                                                        {...columnProps} 
                                                        className={`col-${column.id} ${column.isSorted ? 'sorted' : ''}`}
                                                        data-sortable={!column.disableSortBy}
                                                        data-sorted={column.isSorted}
                                                        aria-sort={
                                                            column.isSorted 
                                                                ? (column.isSortedDesc ? 'descending' : 'ascending')
                                                                : 'none'
                                                        }
                                                    >
                                                        <div className="header-content">
                                                            <span className="header-text">{column.render('Header')}</span>
                                                            {!column.disableSortBy && (
                                                                <span className={`sort-icon ${column.isSorted ? 'active' : ''}`}>
                                                                    {column.isSorted ? (
                                                                        column.isSortedDesc ? 
                                                                            <FontAwesomeIcon icon={faSortDown} title="Sorted Descending" /> : 
                                                                            <FontAwesomeIcon icon={faSortUp} title="Sorted Ascending" />
                                                                    ) : (
                                                                        <FontAwesomeIcon icon={faSort} title="Click to Sort" />
                                                                    )}
                                                                </span>
                                                            )}
                                                            {column.disableSortBy && (
                                                                <span className="sort-icon disabled" style={{opacity: 0.3}}>
                                                                    <i className="fas fa-grip-vertical" title="Not Sortable"></i>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.map(row => {
                                    prepareRow(row);
                                    const { key: rowKey, ...rowProps } = row.getRowProps();
                                    return (
                                        <tr {...rowProps} 
                                            className="triage-project-table-row" 
                                            onClick={() => handleShowModal(row.original)}
                                        >
                                            {row.cells.map(cell => {
                                                const { key: cellKey, ...cellProps } = cell.getCellProps();
                                                return (
                                                    <td {...cellProps}>{cell.render('Cell')}</td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {pageSize !== 'All' && totalPages > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        Page {currentPage + 1} of {totalPages}
                    </div>
                    <div className="pagination-controls">
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(0)}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 2) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 3) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 1 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    className={`pagination-btn ${pageNum === currentPage + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(pageNum - 1)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        <button 
                            className="pagination-btn"
                            onClick={nextPage}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(totalPages - 1)}
                            disabled={!canNextPage}
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            )}

            {activeModals.map((modal) => (
                modal.isOpen && (
                <TriageProjectModal
                    key={modal.id}
                    data={modal.data}
                    show={modal.isOpen}
                    onClose={() => handleCloseModal(modal.id)}
                    allowMove={true}
                        style={{
                            top: modal.top,
                            left: modal.left,
                            zIndex: modal.zIndex
                        }}
                    />
                )
            ))}
        </div>
    );
};

export default TriageProjectTable;
